<template>
  <label
    class="pb-beams-cell"
    :class="{
      'pb-beams-cell-select-mode': isSelectMode(),
      'pb-beams-cell-edit-mode': isEditMode(),
      'pb-beams-cell-with-beam': !!normalizedBeam,
      'pb-beams-cell-without-beam': !normalizedBeam,
      'pb-beams-cell-disabled': cellDisabled,
      'pb-beams-cell-enabled': !cellDisabled,
      'pb-beams-cell-selected': checked,
      'pb-beams-cell-without-cluster': !clusterId,
      'pb-beams-cell-with-cluster': !!clusterId,
    }"
    :style="clusterId ? {
      '--beam-cluster-color': `var(--cluster-color-${clusterId})`,
      '--beam-cluster-color-focus': `var(--cluster-color-focus-${clusterId})`,
      '--beam-cluster-color-deselected': `var(--cluster-color-deselected-${clusterId})`,
      '--beam-cluster-color-disabled': `var(--cluster-color-disabled-${clusterId})`,
    } : {}"
  >
    <input
      type="checkbox"
      class="pb-beams-cell-checkbox"
      :checked="checked"
      :disabled="cellDisabled"
      @change="changed"
    >
    <div class="pb-beams-cell-content">
      <template v-if="normalizedBeam">
        {{ beamId }}
      </template>
    </div>
  </label>
</template>

<script setup lang="ts">
// name: 'BeamsCell'

import { computed } from 'vue'
import type { Beam, SelectableBeam } from './beamsTypes.ts'
import { MAX_CLUSTERS } from './beamsEditorComposables.ts'
 
defineOptions({
  compatConfig: {
    MODE: 3, // opt-in to Vue 3 behavior for this component only
  },
})

interface Props {
  beam?: Beam | SelectableBeam | null,
  /**
   * Select (true) mode is when selecting beams affected by a channel
   * Editing (false) mode is when editing beams of a profile
   */
  mode?: 'edit' | 'select'
  loading?: boolean,
  disabled?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
  beam: null,
  mode: 'edit',
  loading: false,
  disabled: false,
})

const emit = defineEmits<{
  change: [checked: boolean],
  input: [checked: boolean],
}>()

function isSelectMode() {
  return props.mode === 'select'
}

function isEditMode() {
  return props.mode === 'edit'
}

const normalizedBeam = computed(() => {
  const { beam } = props
  if (!beam) return null
  return {
    ...beam,
    selected: 'selected' in beam ? beam.selected : false,
    disabled: 'disabled' in beam ? beam.disabled : false,
    clusterId: 'clusterId' in beam ? beam.clusterId : undefined
  }
})

const checked = computed(() => {
  // In select and edit mode, a cell is "checked":
  // - when it has a beam
  // - and the beam has the "selected" property set to true
  if (!normalizedBeam.value) return false
  return normalizedBeam.value.selected
})

/**
 * In select mode a cell is "disabled":
 * - if it has no beam
 * - if the beam is disabled (for example because it's the last selected beam)
 * - if the grid is loading
 * - if the grid is disabled
 * 
 * In editing mode, a cell is "disabled":
 * - if it has a beam and it's disabled
 * - if the grid is loading
 * - if the grid is disabled
 */
const cellDisabled = computed(() => {
  if ((isSelectMode()) && !normalizedBeam.value) return true
  return normalizedBeam.value?.disabled || props.loading || props.disabled
})

const beamId = computed(() => (
  normalizedBeam.value?.id
))

const clusterId = computed(() => {
  const clusterIdValue = normalizedBeam.value?.clusterId
  if (typeof clusterIdValue !== 'number') return null
  if (clusterIdValue < 0 || clusterIdValue >= MAX_CLUSTERS) return null
  return String(clusterIdValue + 1).padStart(2, '0')
})

function changed(event: Event) {
  const newChecked = (event.target as HTMLInputElement).checked
  emit('change', newChecked)
  emit('input', newChecked)
}
</script>
